import type { ComponentProps, ReactNode } from 'react';
import { DropdownMenu } from 'radix-ui';
import { Check } from 'lucide-react';
import { Kbd } from './Kbd';

/** A dropdown menu: `Menu.Root` > `Menu.Trigger` + `Menu.Content` > `Menu.Item`… */
export const Menu = {
  // Non-modal: items often open dialogs, and a modal menu returning focus would close them.
  Root(props: ComponentProps<typeof DropdownMenu.Root>) {
    return <DropdownMenu.Root modal={false} {...props} />;
  },
  Trigger: DropdownMenu.Trigger,
  Content({ children, align = 'start', side, sideOffset = 6 }: { children: ReactNode; align?: 'start' | 'end' | 'center'; side?: 'top' | 'bottom' | 'left' | 'right'; sideOffset?: number }) {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align={align} side={side} sideOffset={sideOffset} collisionPadding={12}>
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    );
  },
  Item({ children, onSelect, danger, disabled, icon, keys }: { children: ReactNode; onSelect?: () => void; danger?: boolean; disabled?: boolean; icon?: ReactNode; keys?: string[] }) {
    return (
      <DropdownMenu.Item className={`menu-item${danger ? ' danger' : ''}`} onSelect={onSelect} disabled={disabled}>
        {icon}
        <span>{children}</span>
        {keys && <Kbd keys={keys} />}
      </DropdownMenu.Item>
    );
  },
  /** A toggle that keeps the menu open, with a check mark when on. */
  CheckboxItem({ children, checked, disabled, onCheckedChange }: { children: ReactNode; checked: boolean; disabled?: boolean; onCheckedChange: (checked: boolean) => void }) {
    return (
      <DropdownMenu.CheckboxItem className="menu-item" checked={checked} disabled={disabled} onSelect={(e) => e.preventDefault()} onCheckedChange={(v) => onCheckedChange(v === true)}>
        <span style={{ width: 16, display: 'inline-flex' }}>{checked && <Check size={16} />}</span>
        <span>{children}</span>
      </DropdownMenu.CheckboxItem>
    );
  },
  Separator() {
    return <DropdownMenu.Separator className="menu-sep" />;
  },
  Label({ children }: { children: ReactNode }) {
    return <DropdownMenu.Label className="menu-label">{children}</DropdownMenu.Label>;
  },
  Sub: DropdownMenu.Sub,
  SubTrigger({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
    return (
      <DropdownMenu.SubTrigger className="menu-item">
        {icon}
        <span>{children}</span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-3)' }}>›</span>
      </DropdownMenu.SubTrigger>
    );
  },
  SubContent({ children }: { children: ReactNode }) {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="menu" sideOffset={6} collisionPadding={12}>
          {children}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    );
  },
};
